import React, { useState, useMemo, useCallback } from "react";
import { useCollection } from "@amzn/awsui-collection-hooks";
import Table from "@amzn/awsui-components-react/polaris/table";
import Header from "@amzn/awsui-components-react/polaris/header";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import PropertyFilter from "@amzn/awsui-components-react/polaris/property-filter";
import Pagination from "@amzn/awsui-components-react/polaris/pagination";
import CollectionPreferences, {
  CollectionPreferencesProps,
} from "@amzn/awsui-components-react/polaris/collection-preferences";
import Box from "@amzn/awsui-components-react/polaris/box";
import Container from "@amzn/awsui-components-react/polaris/container";
import ButtonDropdown, {
  ButtonDropdownProps,
} from "@amzn/awsui-components-react/polaris/button-dropdown";
import { Document } from "models/schemas";

import { useListAtlasDocumentsQuery } from "pages/gaia-ai/endpoint";
import { useNotifications } from "features/notifications/hooks";
import { useLocalStorage, useColumnWidths } from "hooks";
import InputFilesModal from "components/input-files-modal";
import EmptyState from "components/empty-state";
import { CommonDeleteModal } from "components/common-reuse-components/common-delete-modal";
import {
  deleteInfoNew,
  getDeleteMessageNew,
  getFileNamesNew,
} from "components/documents-table-os/utils";
import {
  commonPropertyFilterI18nStrings,
  paginationLabels,
  getMatchesCountText,
} from "utils/table-utils";
import { IAtlasDocumentsTableProps } from "./definitions";
import { adaptAtlasDocumentToDocument } from "./utils";
import { useAtlasFileOperations } from "./file-operations";
import { createAtlasColumnDefinitions } from "./column-definitions";
import {
  createAtlasFilterProperties,
  pageSizePreferenceAtlasKB,
} from "./table-config";
import { useSelectedTeam } from "features/team-settings/hooks";
import { useFilePreviewSplitPanel } from "components/file-preview-split-panel/hooks";

export const ACCEPTED_FILE_TYPES =
  ".txt, .pdf, .docx, .doc, .html, .htm, .ppt, .pptx, .png, .jpg, .jpeg, .csv, .tsv, .xlsx";

export const ANSWERS_KB_SUPPORTED_EXTENSIONS: ReadonlySet<string> = new Set(
  ACCEPTED_FILE_TYPES.split(",").map((e) => e.trim().toLowerCase()),
);

export const isAnswersKbSupportedExtension = (ext?: string): boolean => {
  if (!ext) return false;
  const normalized = ext.startsWith(".")
    ? ext.toLowerCase()
    : `.${ext.toLowerCase()}`;
  return ANSWERS_KB_SUPPORTED_EXTENSIONS.has(normalized);
};

const AtlasDocumentsTable: React.FC<IAtlasDocumentsTableProps> = ({
  teamName,
  onUploadClick,
}) => {
  const { addNotification } = useNotifications();
  const { isKBApprovalEnabled, isKBApprovalDeletionEnabled } =
    useSelectedTeam();

  // State
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [addDocModalVisible, setAddDocModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [singleDocToDelete, setSingleDocToDelete] = useState<
    Document | undefined
  >();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // API hooks
  const {
    data: atlasData,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useListAtlasDocumentsQuery(
    { teamName, includeApprovalInfo: isKBApprovalEnabled },
    { skip: !teamName, refetchOnMountOrArgChange: true },
  );

  // File operations
  const {
    uploadAtlasFiles,
    deleteAtlasDoc,
    deleteAtlasDocs,
    downloadSingleDoc,
    downloadDocs,
  } = useAtlasFileOperations({ teamName, addNotification });

  // File preview split panel
  const { setSelectedDocumentId, setSplitPanelOpen, setSource } =
    useFilePreviewSplitPanel();

  const handlePreviewClick = useCallback(
    (doc: Document) => {
      setSelectedDocumentId(doc.documentId);
      setSource("atlas");
      setSplitPanelOpen(true);
    },
    [setSelectedDocumentId, setSource, setSplitPanelOpen],
  );

  // Adapt API data to display format
  const atlasDocuments = useMemo(
    () => (atlasData?.documents || []).map(adaptAtlasDocumentToDocument),
    [atlasData],
  );

  // Filter properties (dynamic based on current docs)
  const filteringProperties = useMemo(
    () => createAtlasFilterProperties(atlasDocuments),
    [atlasDocuments],
  );

  // Filter persistence
  const [filteringState, setFilteringState] = useLocalStorage<any>(
    `${teamName}-AtlasDocuments-Filters`,
    { tokens: [], operation: "and" },
  );

  // Action click handler for row-level dropdown
  const handleItemActionClick = useCallback(
    (event: CustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
      const dotIndex = event.detail.id.indexOf(".");
      const action = event.detail.id.substring(0, dotIndex);
      const docId = event.detail.id.substring(dotIndex + 1);
      const doc = atlasDocuments.find((d) => d.documentId === docId);
      if (!doc) return;

      if (action === "dl") {
        downloadSingleDoc(doc);
      } else if (action === "rm") {
        setSingleDocToDelete(doc);
        setDeleteModalVisible(true);
      }
    },
    [atlasDocuments, downloadSingleDoc],
  );

  // Column definitions
  const rawColumnDefinitions = useMemo(
    () =>
      createAtlasColumnDefinitions({
        handleItemActionClick,
        downloadSingleDoc,
        onPreview: handlePreviewClick,
        showApprovalBatch: isKBApprovalEnabled,
      }),
    [
      handleItemActionClick,
      downloadSingleDoc,
      handlePreviewClick,
      isKBApprovalEnabled,
    ],
  );

  const [columnDefinitions, saveWidths] = useColumnWidths(
    `${teamName}-atlas-DocumentsTable-Widths`,
    rawColumnDefinitions,
  );

  // Preferences
  const [preferences, setPreferences] = useLocalStorage(
    `${teamName}-atlas-DocumentsTable-Preferences`,
    {
      pageSize: 50,
      wrapLines: true,
      stripedRows: true,
      contentDisplay: rawColumnDefinitions.map((col) => ({
        id: col.id || "",
        visible: true,
      })),
    },
  );

  // Collection hook for client-side filtering/sorting/pagination
  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    propertyFilterProps,
    paginationProps,
  } = useCollection(atlasDocuments, {
    propertyFiltering: {
      filteringProperties,
      defaultQuery: filteringState,
      empty: (
        <EmptyState
          title="No files"
          subtitle="No files to display."
          action={
            <Button
              onClick={() =>
                onUploadClick ? onUploadClick() : setAddDocModalVisible(true)
              }
            >
              Upload
            </Button>
          }
        />
      ),
      noMatch: (
        <EmptyState
          title="No matches"
          subtitle="We can't find a match."
          action={
            <Button
              onClick={() =>
                actions.setPropertyFiltering({ tokens: [], operation: "and" })
              }
            >
              Clear filter
            </Button>
          }
        />
      ),
    },
    pagination: { pageSize: (preferences as any).pageSize },
    sorting: {
      defaultState: {
        sortingColumn: rawColumnDefinitions.find(
          (col) => col.sortingField === "modifiedAt",
        )!,
        isDescending: true,
      },
    },
  });

  // Refresh handler
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  }, [refetch]);

  // Delete confirmation handler
  const handleDeleteConfirm = useCallback(
    async (docsToDelete?: any[]) => {
      const docs =
        docsToDelete ||
        (singleDocToDelete ? [singleDocToDelete] : selectedItems);

      try {
        if (docs.length === 1) {
          await deleteAtlasDoc(docs[0]);
        } else {
          await deleteAtlasDocs(docs);
        }
        // KB approval flow handles its own notifications; skip standard ones
        if (!isKBApprovalDeletionEnabled) {
          addNotification({
            type: "success",
            header: `${docs.length} file${
              docs.length > 1 ? "s" : ""
            } deleted successfully.`,
            dismissible: true,
            timeout: 10000,
          });
        }
      } catch {
        if (!isKBApprovalDeletionEnabled) {
          addNotification({
            type: "error",
            header: "Failed to delete document(s).",
            dismissible: true,
            timeout: 10000,
          });
        }
      } finally {
        setSelectedItems([]);
        setSingleDocToDelete(undefined);
        setDeleteModalVisible(false);
      }
    },
    [
      singleDocToDelete,
      selectedItems,
      deleteAtlasDoc,
      deleteAtlasDocs,
      addNotification,
      isKBApprovalDeletionEnabled,
    ],
  );

  // Bulk actions handler
  const handleActionsClick = useCallback(
    (event: CustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
      if (event.detail.id === "download") {
        downloadDocs(selectedItems);
      } else if (event.detail.id === "delete") {
        setSingleDocToDelete(undefined);
        setDeleteModalVisible(true);
      }
    },
    [selectedItems, downloadDocs],
  );

  const docsToDelete = singleDocToDelete ? [singleDocToDelete] : selectedItems;

  const collectionPreferencesProps: CollectionPreferencesProps = {
    pageSizePreference: pageSizePreferenceAtlasKB,
    cancelLabel: "Cancel",
    confirmLabel: "Confirm",
    title: "Preferences",
  };

  if (isError) {
    return (
      <Container>
        <Box textAlign="center">
          <b>Error loading documents. Please contact the Gaia Team.</b>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Table
        {...collectionProps}
        variant="container"
        columnDefinitions={columnDefinitions}
        items={items}
        loading={isLoading}
        loadingText="Loading documents..."
        selectionType="multi"
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) =>
          setSelectedItems(detail.selectedItems)
        }
        onColumnWidthsChange={saveWidths}
        wrapLines={(preferences as any).wrapLines}
        stripedRows={(preferences as any).stripedRows}
        trackBy="documentId"
        stickyColumns={(preferences as any).stickyColumns}
        resizableColumns
        enableKeyboardNavigation
        renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
          `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
        }
        ariaLabels={{
          selectionGroupLabel: "Items selection",
          allItemsSelectionLabel: ({ selectedItems }) =>
            `${selectedItems.length} ${
              selectedItems.length === 1 ? "item" : "items"
            } selected`,
          // biome-ignore lint/correctness/noEmptyPattern: existing pattern
          itemSelectionLabel: ({}, item: any) => item.documentId,
        }}
        header={
          <Header
            counter={`(${atlasDocuments.length})`}
            actions={
              <SpaceBetween size="s" direction="horizontal">
                <Button
                  iconName="refresh"
                  onClick={handleRefresh}
                  loading={isRefreshing}
                  ariaLabel="Refresh documents"
                />
                <ButtonDropdown
                  items={[
                    {
                      id: "download",
                      text: "Download",
                      disabled: !selectedItems.length,
                      disabledReason: "No document is selected.",
                    },
                    {
                      id: "delete",
                      text: "Delete",
                      disabled:
                        !selectedItems.length ||
                        selectedItems.some(
                          (item: any) =>
                            !["INDEXED", "FAILED"].includes(item.parsingStatus),
                        ),
                      disabledReason:
                        "Only INDEXED or FAILED documents may be deleted",
                    },
                  ]}
                  onItemClick={handleActionsClick}
                  expandToViewport
                >
                  Files actions
                </ButtonDropdown>
                <Button
                  variant="primary"
                  onClick={() =>
                    onUploadClick
                      ? onUploadClick()
                      : setAddDocModalVisible(true)
                  }
                >
                  Upload
                </Button>
              </SpaceBetween>
            }
          >
            Files
          </Header>
        }
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            enableTokenGroups
            expandToViewport
            countText={getMatchesCountText(filteredItemsCount)}
            i18nStrings={{
              ...commonPropertyFilterI18nStrings,
              filteringPlaceholder: "Find documents",
              tokenEditorAddNewTokenLabel: "Add new filter",
            }}
            onChange={({ detail }) => {
              propertyFilterProps.onChange({ detail });
              setFilteringState(detail);
            }}
          />
        }
        pagination={
          <Pagination {...paginationProps} ariaLabels={paginationLabels} />
        }
        preferences={
          <CollectionPreferences
            {...collectionPreferencesProps}
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail as any)}
            contentDensityPreference={{
              label: "Compact mode",
              description: "Select to display compact mode",
            }}
            stickyColumnsPreference={{
              firstColumns: {
                title: "First column(s)",
                description:
                  "Keep the first column(s) visible while horizontally scrolling table content.",
                options: [
                  { label: "None", value: 0 },
                  { label: "First column", value: 1 },
                  { label: "First two columns", value: 2 },
                ],
              },
              lastColumns: {
                title: "Stick last visible column",
                description:
                  "Keep the last column visible when tables are wider than the viewport.",
                options: [
                  { label: "None", value: 0 },
                  { label: "Last column", value: 1 },
                  { label: "Last two columns", value: 2 },
                ],
              },
            }}
            wrapLinesPreference={{
              label: "Wrap lines",
              description: "Select to see all the text and wrap the lines",
            }}
            stripedRowsPreference={{
              label: "Striped rows",
              description: "Select to add alternating shaded rows",
            }}
          />
        }
      />

      {/* Upload modal */}
      {addDocModalVisible && (
        <InputFilesModal
          onCreate={uploadAtlasFiles}
          visible={addDocModalVisible}
          setVisible={setAddDocModalVisible}
          addNotification={addNotification}
          enableRenaming={false}
          enableDocumentTypes={false}
          requireDocumentTypes={false}
          acceptedFileTypes={ACCEPTED_FILE_TYPES}
          existingDocuments={atlasDocuments}
          enableReplaceMode={true}
          versioningEnabled={false}
        />
      )}

      {/* Delete confirmation modal */}
      <CommonDeleteModal
        visible={deleteModalVisible}
        setVisible={setDeleteModalVisible}
        onDelete={handleDeleteConfirm}
        items={docsToDelete}
        title={deleteInfoNew(docsToDelete, "?")}
        message={getDeleteMessageNew(
          docsToDelete,
          getFileNamesNew(docsToDelete),
        )}
      />
    </>
  );
};

export default AtlasDocumentsTable;
